import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, CheckCircle2, ClipboardList, Plus, Printer, Trash2 } from "lucide-react";
import { api, ApiError } from "../../api/client";
import RatingScale from "./RatingScale";
import TimeframePicker, { timeframeLabel } from "./TimeframePicker";
import CheckInPicker, { cadenceLabel, type CheckInValue } from "./CheckInPicker";
import MeetingPanel, { type MeetingInfo } from "./MeetingPanel";
import { ObjectivesHighlight } from "./Objectives";
import { appraisalName, ratingScale, selfRatingText } from "../../utils/appraisal";

/* ------------------------------------------------------------------ types */

interface Row { [k: string]: unknown }

export interface MeetingReview {
  id: number;
  employee_name: string;
  manager_name: string | null;
  status: string;
  appraisal_year?: number | null;
  rating_words?: boolean;
  meeting?: MeetingInfo | null;
  overall_self_rating: number | null;
  overall_manager_rating: number | null;
  overall_agreed_rating: number | null;
  overall_example?: string | null;
  calibration_summary: string | null;
  top_priority_1: string | null;
  top_priority_2: string | null;
  could_do_better: string | null;
  role_aspirations: string | null;
  additional_comments: string | null;
  check_in_cadence: string | null;
  check_in_minutes?: number | null;
  check_in_time?: string | null;
  meeting_notes?: string | null;
  meeting_section_notes?: Record<string, string> | null;
  responsibilities: Row[];
  strengths: Row[];
  development_areas: Row[];
  behaviours: Row[];
  objectives: Row[];
  development_plan: Row[];
  feedback_themes: Row[];
}

interface Objective { objective: string; how_measured: string; timeframe: string }

const str = (v: unknown) => (typeof v === "string" ? v : "");
const num = (v: unknown) => (typeof v === "number" ? v : null);

/* --------------------------------------------------------------- the agenda */

interface Item { key: string; title: string; minutes: number; prompt: string }

const AGENDA: Item[] = [
  { key: "start", title: "Welcome", minutes: 2, prompt: "Thank them for their self-assessment. Explain the plan: their view first, then yours, then agree the rating and what comes next." },
  { key: "their_year", title: "Their view of the year", minutes: 8, prompt: "Ask them to talk you through their year. Listen first; ask what went well and what was hard." },
  { key: "summary", title: "Your view of the year", minutes: 5, prompt: "Share your summary. Be specific and kind." },
  { key: "responsibilities", title: "Responsibilities", minutes: 6, prompt: "Go through where your view and theirs differ. Use your examples." },
  { key: "strengths", title: "Strengths", minutes: 3, prompt: "Say what you value. Ask how they'd like to use these strengths more." },
  { key: "development", title: "What to develop", minutes: 4, prompt: "Agree one or two things to work on. Ask what support would help." },
  { key: "colleagues", title: "What colleagues said", minutes: 3, prompt: "Share the themes, not who said what." },
  { key: "behaviours", title: "Behaviours", minutes: 3, prompt: "Pick out the one or two that matter most." },
  { key: "rating", title: "Agree the rating", minutes: 3, prompt: "Agree the final rating together. A 3 is a solid job. A 4 or 5 is backed by the examples you wrote." },
  { key: "objectives", title: "Objectives for next year", minutes: 5, prompt: "Agree what they will deliver, how you'll both know, and by when." },
  { key: "plan", title: "Development plan", minutes: 2, prompt: "Agree the support: training, time, coaching, and who arranges it." },
  { key: "check_ins", title: "Catch-ups", minutes: 1, prompt: "Agree how often you'll catch up. Keep them short: 15 minutes is usually enough." },
  { key: "close", title: "Anything else", minutes: 2, prompt: "Ask if there's anything else they want to raise. Explain what happens next." },
];

/* -------------------------------------------------------------- small parts */

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="whitespace-pre-line text-sm text-gray-800">{value}</p>
    </div>
  );
}

function ratingName(n: number | null | undefined): string {
  if (n == null) return "";
  const r = ratingScale().find((x) => x.value === n);
  return r ? `${n} · ${r.label}` : String(n);
}

/* ------------------------------------------------------------ the component */

export default function MeetingGuide({ reviewId }: { reviewId: number }) {
  const [review, setReview] = useState<MeetingReview | null>(null);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState("");
  const [agreed, setAgreed] = useState<number | null>(null);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [checkIn, setCheckIn] = useState<CheckInValue>({ cadence: "", minutes: 15, time: "09:30" });
  const [noCheckIns, setNoCheckIns] = useState(false);
  const [summary, setSummary] = useState("");
  const [release, setRelease] = useState(true);
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState("");
  const [done, setDone] = useState<{ firstCheckIn: string | null; justNow?: boolean } | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await api.get<MeetingReview>(`/api/reviews/${reviewId}/manager-review`);
        if (cancelled) return;
        setReview(r);
        setNotes(r.meeting_section_notes ?? {});
        setAgreed(r.overall_agreed_rating ?? r.overall_manager_rating ?? null);
        const agreedObjs = r.objectives.filter((o) => o.source === "manager_agreed");
        setObjectives(
          (agreedObjs.length > 0 ? agreedObjs : [{}]).map((o) => ({
            objective: str(o.objective), how_measured: str(o.how_measured), timeframe: str(o.timeframe),
          })),
        );
        setCheckIn({
          cadence: r.check_in_cadence ?? "fortnightly",
          minutes: r.check_in_minutes ?? 15,
          time: r.check_in_time ?? "09:30",
        });
        setSummary(r.meeting_notes ?? "");
        if (r.status === "completed") setDone({ firstCheckIn: null });
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Couldn't load the appraisal.");
      }
    })();
    return () => { cancelled = true; };
  }, [reviewId]);

  const saveNotes = useCallback(async (next: Record<string, string>) => {
    try {
      const res = await api.put<{ saved_at: string }>(`/api/reviews/${reviewId}/meeting-notes`, { section_notes: next });
      setSavedAt(res.saved_at);
      setSaveError("");
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Notes not saved. Check your connection.");
    }
  }, [reviewId]);

  const setNote = (key: string, value: string) => {
    const next = { ...notes, [key]: value };
    setNotes(next);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void saveNotes(next), 1200);
  };

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const totalMinutes = useMemo(() => AGENDA.reduce((n, a) => n + a.minutes, 0), []);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!review) return <p className="text-sm text-gray-400">Loading the meeting guide…</p>;

  const first = review.employee_name.split(" ")[0];
  const words = review.rating_words;
  const theirStrengths = review.strengths.filter((s) => s.source !== "manager");
  const yourStrengths = review.strengths.filter((s) => s.source === "manager");
  const theirDev = review.development_areas.filter((d) => d.source !== "manager");
  const yourDev = review.development_areas.filter((d) => d.source === "manager");
  const theirObjectives = review.objectives.filter((o) => o.source !== "manager_agreed" && str(o.objective));
  const finished = done != null || review.status === "completed";

  const buildSummary = () => {
    const parts: string[] = [];
    for (const a of AGENDA) {
      const n = (notes[a.key] ?? "").trim();
      if (n) parts.push(`${a.title}: ${n}`);
    }
    setSummary(parts.join("\n\n"));
  };

  const finish = async () => {
    setFinishError("");
    if (!agreed) { setFinishError("Agree the final rating first (section 9)."); return; }
    setFinishing(true);
    try {
      if (timer.current) { window.clearTimeout(timer.current); await saveNotes(notes); }
      const meetingDay = review.meeting?.local_date;
      const today = new Date().toISOString().slice(0, 10);
      const res = await api.post<{ first_check_in: string | null }>(`/api/reviews/${reviewId}/complete`, {
        meeting_date: meetingDay && meetingDay <= today ? meetingDay : today,
        overall_agreed_rating: agreed,
        meeting_notes: summary,
        section_notes: notes,
        confirmed_objectives: objectives.filter((o) => o.objective.trim()),
        check_in_cadence: noCheckIns ? null : checkIn.cadence || null,
        check_in_minutes: checkIn.minutes,
        check_in_time: checkIn.time,
        release_feedback: release,
      });
      setDone({ firstCheckIn: res.first_check_in ?? null, justNow: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setFinishError(err instanceof ApiError ? err.message : "Couldn't finish the appraisal. Please try again.");
    } finally {
      setFinishing(false);
    }
  };

  const section = (n: number, item: Item, body: React.ReactNode) => (
    <section key={item.key} className="meeting-section break-inside-avoid rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">
          <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-pme-navy text-[11px] text-white">{n}</span>
          {item.title}
        </h3>
        <span className="shrink-0 text-[11px] text-gray-400">about {item.minutes} min</span>
      </div>
      <p className="mt-1 text-xs text-pme-blue">{item.prompt}</p>
      <div className="mt-3 space-y-3">{body}</div>
      {!finished ? (
        <label className="mt-3 block print:hidden">
          <span className="text-[11px] font-semibold text-gray-500">Your notes</span>
          <textarea
            rows={2}
            value={notes[item.key] ?? ""}
            onChange={(e) => setNote(item.key, e.target.value)}
            placeholder="What was said or agreed"
            className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none"
          />
        </label>
      ) : null}
      {(notes[item.key] ?? "").trim() && (
        <p className={`mt-2 whitespace-pre-line rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700 ${finished ? "" : "hidden print:block"}`}>
          <span className="font-semibold">Notes: </span>{notes[item.key]}
        </p>
      )}
      {/* room to write by hand on a printed copy */}
      {!(notes[item.key] ?? "").trim() && <div className="mt-2 hidden h-12 border-b border-dashed border-gray-300 print:block" />}
    </section>
  );

  const ratedList = (rows: Row[], nameKey: string, theirsKey: string, theirRatingKey: string) => (
    rows.length === 0 ? <p className="text-xs italic text-gray-400">Nothing entered.</p> : (
      <ul className="space-y-2">
        {rows.map((r, i) => (
          <li key={i} className="rounded-lg bg-gray-50 p-2.5">
            <p className="text-sm font-semibold text-gray-800">{str(r[nameKey])}</p>
            <div className="mt-1 grid gap-2 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-semibold text-blue-700">Their view{num(r[theirRatingKey]) != null ? `: ${selfRatingText(num(r[theirRatingKey]), words)}` : ""}</p>
                <p className="whitespace-pre-line text-xs text-gray-700">{str(r[theirsKey]) || "—"}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold text-purple-700">Your view{num(r.manager_rating) != null ? `: ${ratingName(num(r.manager_rating))}` : ""}</p>
                <p className="whitespace-pre-line text-xs text-gray-700">{str(r.manager_evidence) || "—"}</p>
                {str(r.manager_example) && <p className="mt-1 whitespace-pre-line text-xs text-gray-700"><span className="font-semibold">Example: </span>{str(r.manager_example)}</p>}
              </div>
            </div>
          </li>
        ))}
      </ul>
    )
  );

  return (
    <div className="space-y-4">
      <style>{`@media print { .meeting-noprint, header, nav { display: none !important; } body { background: white; } .meeting-section { border-color: #ccc; } }`}</style>

      {/* Heading */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Meeting guide · {appraisalName(review.appraisal_year)}</p>
        <h2 className="mt-0.5 text-lg font-semibold text-gray-900">{review.employee_name}</h2>
        <p className="text-xs text-gray-600">
          Go through it together, top to bottom. About {totalMinutes} minutes. Your notes save as you type and are only for you
          and HR; what you write in the summary at the end is shared with {first}.
        </p>
        <div className="meeting-noprint mt-3 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => window.print()} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50">
            <Printer size={14} aria-hidden /> Print the guide
          </button>
          {savedAt && !saveError && <span className="text-[11px] text-green-700">Notes saved</span>}
          {saveError && <span role="alert" className="text-[11px] text-red-600">{saveError}</span>}
        </div>
      </div>

      {finished ? (
        <div className="meeting-noprint rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-green-800">
            <CheckCircle2 size={18} aria-hidden /> Appraisal complete
          </p>
          <p className="mt-1 text-xs text-green-900">
            {!done?.justNow
              ? "This appraisal has been finished. Below is what was discussed and agreed."
              : release ? `${first} can now see your feedback, the agreed rating and their objectives.` : "The feedback isn't shared yet. You can share it from the appraisal page."}
            {done?.firstCheckIn ? ` Your first catch-up is on ${new Date(`${done.firstCheckIn}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}.` : ""}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {(review.check_in_cadence || checkIn.cadence) && !noCheckIns && (
              <a href={`/api/check-ins/plans/${reviewId}/series.ics`} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-pme-blue px-3 text-xs font-semibold text-white">
                <CalendarPlus size={14} aria-hidden /> Add catch-ups to my calendar
              </a>
            )}
            <Link to="/team" className="inline-flex min-h-[40px] items-center rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700">
              Go to My team
            </Link>
          </div>
        </div>
      ) : (
        <div className="meeting-noprint">
          <MeetingPanel reviewId={review.id} employeeName={review.employee_name} status={review.status} meeting={review.meeting} compact inGuide />
        </div>
      )}

      {section(1, AGENDA[0], <p className="text-xs text-gray-600">Set them at ease. It's a conversation, not a test.</p>)}

      {section(2, AGENDA[1], (
        <div className="space-y-2">
          <Field label="Their overall view" value={review.overall_self_rating ? selfRatingText(review.overall_self_rating, words) : null} />
          <Field label="Top priority" value={review.top_priority_1} />
          <Field label="Second priority" value={review.top_priority_2} />
          <Field label="What they'd do differently" value={review.could_do_better} />
          <Field label="Where they'd like to go next" value={review.role_aspirations} />
          <Field label="Anything else they said" value={review.additional_comments} />
        </div>
      ))}

      {section(3, AGENDA[2], <Field label="Your summary" value={review.calibration_summary || "—"} />)}

      {section(4, AGENDA[3], ratedList(review.responsibilities, "responsibility_area", "employee_summary", "self_rating"))}

      {section(5, AGENDA[4], (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-[11px] font-semibold text-blue-700">They said</p>
            <ul className="mt-0.5 space-y-1 text-xs text-gray-700">
              {theirStrengths.length === 0 ? <li className="italic text-gray-400">Nothing entered.</li> : theirStrengths.map((s, i) => <li key={i}><span className="font-semibold">{str(s.strength)}</span>{str(s.still_valid_example) ? `: ${str(s.still_valid_example)}` : ""}</li>)}
            </ul>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-purple-700">You said</p>
            <ul className="mt-0.5 space-y-1 text-xs text-gray-700">
              {yourStrengths.length === 0 ? <li className="italic text-gray-400">Nothing entered.</li> : yourStrengths.map((s, i) => <li key={i}><span className="font-semibold">{str(s.strength)}</span>{str(s.evidence_impact) ? `: ${str(s.evidence_impact)}` : ""}</li>)}
            </ul>
          </div>
        </div>
      ))}

      {section(6, AGENDA[5], (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-[11px] font-semibold text-blue-700">They said</p>
            <ul className="mt-0.5 space-y-1 text-xs text-gray-700">
              {theirDev.length === 0 ? <li className="italic text-gray-400">Nothing entered.</li> : theirDev.map((d, i) => <li key={i}><span className="font-semibold">{str(d.area)}</span>{str(d.employee_progress) ? `: ${str(d.employee_progress)}` : ""}</li>)}
            </ul>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-purple-700">You said</p>
            <ul className="mt-0.5 space-y-1 text-xs text-gray-700">
              {yourDev.length === 0 ? <li className="italic text-gray-400">Nothing entered.</li> : yourDev.map((d, i) => (
                <li key={i}><span className="font-semibold">{str(d.area)}</span>{str(d.manager_current_state) ? `: now ${str(d.manager_current_state)}` : ""}{str(d.manager_what_good_looks_like) ? `; good looks like ${str(d.manager_what_good_looks_like)}` : ""}</li>
              ))}
            </ul>
          </div>
        </div>
      ))}

      {section(7, AGENDA[6], (
        review.feedback_themes.filter((t) => str(t.theme)).length === 0
          ? <p className="text-xs italic text-gray-400">No colleague themes written up.</p>
          : (
            <ul className="space-y-1.5 text-xs text-gray-700">
              {review.feedback_themes.filter((t) => str(t.theme)).map((t, i) => (
                <li key={i}><span className="font-semibold">{str(t.theme)}</span>{str(t.manager_view) ? `: ${str(t.manager_view)}` : ""}</li>
              ))}
            </ul>
          )
      ))}

      {section(8, AGENDA[7], ratedList(review.behaviours, "behaviour_name", "employee_example", "employee_rating"))}

      {section(9, AGENDA[8], (
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="Their view" value={review.overall_self_rating ? selfRatingText(review.overall_self_rating, words) : "—"} />
            <Field label="Your rating before the meeting" value={ratingName(review.overall_manager_rating) || "—"} />
          </div>
          <Field label="Why (your example)" value={review.overall_example} />
          {!finished ? (
            <div>
              <p className="mb-1 text-xs font-semibold text-gray-800">The rating you agree</p>
              <RatingScale value={agreed} onChange={setAgreed} scale="company" />
              <p className="mt-1 text-[11px] text-gray-500">Change it if the conversation showed something new. A 4 or 5 should match the examples you wrote.</p>
            </div>
          ) : (
            <Field label="Agreed rating" value={ratingName(agreed)} />
          )}
        </div>
      ))}

      {section(10, AGENDA[9], (
        <div className="space-y-3">
          <div className="meeting-noprint"><ObjectivesHighlight name={first} /></div>
          <p className="text-[11px] text-gray-500">
            <span className="font-semibold">Objectives</span> are what they will deliver at work. The development plan (next) is the help they get to grow.
          </p>
          {theirObjectives.length > 0 && !finished && (
            <div className="meeting-noprint">
              <p className="text-[11px] font-semibold text-blue-700">They suggested (tap to add)</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {theirObjectives.map((o, i) => (
                  <button key={i} type="button"
                    onClick={() => setObjectives((prev) => [...prev.filter((p) => p.objective.trim()), { objective: str(o.objective), how_measured: str(o.how_measured), timeframe: "" }])}
                    className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 text-left text-xs text-blue-800 hover:bg-blue-100">
                    <Plus size={12} aria-hidden /> {str(o.objective)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {objectives.map((o, idx) => (
            <div key={idx} className="rounded-lg border border-gray-100 bg-gray-50 p-2.5">
              {finished ? (
                <p className="text-sm text-gray-800">
                  <span className="font-semibold">{o.objective}</span>
                  {o.how_measured ? ` · measured by ${o.how_measured}` : ""}
                  {o.timeframe ? ` · within ${timeframeLabel(o.timeframe)}` : ""}
                </p>
              ) : (
                <>
                  <div className="flex items-start gap-2">
                    <input
                      value={o.objective}
                      onChange={(e) => setObjectives(objectives.map((x, i) => i === idx ? { ...x, objective: e.target.value } : x))}
                      placeholder="What they will deliver"
                      className="input w-full"
                    />
                    {objectives.length > 1 && (
                      <button type="button" aria-label="Remove objective" onClick={() => setObjectives(objectives.filter((_, i) => i !== idx))}
                        className="meeting-noprint rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-red-600">
                        <Trash2 size={14} aria-hidden />
                      </button>
                    )}
                  </div>
                  <input
                    value={o.how_measured}
                    onChange={(e) => setObjectives(objectives.map((x, i) => i === idx ? { ...x, how_measured: e.target.value } : x))}
                    placeholder="How you'll both know it's done"
                    className="input mt-2 w-full"
                  />
                  <div className="mt-2">
                    <TimeframePicker value={o.timeframe} onChange={(v) => setObjectives(objectives.map((x, i) => i === idx ? { ...x, timeframe: v } : x))} />
                  </div>
                </>
              )}
            </div>
          ))}
          {!finished && (
            <button type="button" onClick={() => setObjectives([...objectives, { objective: "", how_measured: "", timeframe: "" }])}
              className="meeting-noprint inline-flex min-h-[36px] items-center gap-1 text-xs font-semibold text-pme-blue">
              <Plus size={14} aria-hidden /> Add an objective
            </button>
          )}
        </div>
      ))}

      {section(11, AGENDA[10], (
        review.development_plan.filter((p) => str(p.support_needed)).length === 0
          ? <p className="text-xs italic text-gray-400">No development plan written up.</p>
          : (
            <ul className="space-y-1 text-xs text-gray-700">
              {review.development_plan.filter((p) => str(p.support_needed)).map((p, i) => (
                <li key={i}>
                  <span className="font-semibold">{str(p.support_needed)}</span>
                  {str(p.provided_by) ? ` · from ${str(p.provided_by)}` : ""}
                  {str(p.timeframe) ? ` · within ${timeframeLabel(str(p.timeframe))}` : ""}
                </li>
              ))}
            </ul>
          )
      ))}

      {section(12, AGENDA[11], finished ? (
        <Field label="Agreed" value={noCheckIns || !checkIn.cadence ? "No regular catch-ups" : `${cadenceLabel(checkIn.cadence)}, ${checkIn.minutes} minutes, around ${checkIn.time}`} />
      ) : (
        <div className="space-y-2">
          {!noCheckIns && <CheckInPicker value={checkIn} onChange={setCheckIn} />}
          <label className="meeting-noprint flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={noCheckIns} onChange={(e) => setNoCheckIns(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
            No regular catch-ups
          </label>
          {!noCheckIns && (
            <p className="text-[11px] text-gray-500">
              After the meeting we email you the series to add to your calendar, and remind you the day before each one.
              You can move or skip any of them from My team.
            </p>
          )}
        </div>
      ))}

      {section(13, AGENDA[12], (
        <p className="text-xs text-gray-600">
          Tell them what happens next: you'll finish it in PME People today, they'll be able to read the feedback and their
          objectives, and they can add their own comments.
        </p>
      ))}

      {/* Finish */}
      {!finished && (
        <section className="meeting-noprint rounded-xl border-2 border-pme-blue/40 bg-white p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <ClipboardList size={16} aria-hidden /> Finish the appraisal
          </h3>
          <p className="mt-0.5 text-xs text-gray-600">Do this at the end of the meeting, or straight after.</p>
          <ul className="mt-2 space-y-0.5 text-xs text-gray-700">
            <li>Agreed rating: <span className="font-semibold">{agreed ? ratingName(agreed) : "not chosen yet (section 9)"}</span></li>
            <li>Objectives: <span className="font-semibold">{objectives.filter((o) => o.objective.trim()).length}</span></li>
            <li>Catch-ups: <span className="font-semibold">{noCheckIns || !checkIn.cadence ? "none" : `${cadenceLabel(checkIn.cadence).toLowerCase()}, ${checkIn.minutes} min`}</span></li>
          </ul>
          <label className="mt-3 block">
            <span className="flex items-center justify-between gap-2 text-xs font-semibold text-gray-800">
              Meeting summary for {first}
              <button type="button" onClick={buildSummary} className="text-[11px] font-semibold text-pme-blue hover:underline">
                Fill in from my notes
              </button>
            </span>
            <span className="block text-[11px] text-gray-500">Shared with {first}. A few lines on what you agreed.</span>
            <textarea rows={5} value={summary} onChange={(e) => setSummary(e.target.value)} className="input mt-1 w-full" />
          </label>
          <label className="mt-3 flex items-start gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={release} onChange={(e) => setRelease(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
            Share the written feedback with {first} now
          </label>
          {finishError && <p role="alert" className="mt-2 text-xs text-red-600">{finishError}</p>}
          <button
            type="button"
            onClick={() => void finish()}
            disabled={finishing}
            className="mt-3 w-full min-h-[44px] rounded-lg bg-green-600 px-4 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
          >
            {finishing ? "Finishing…" : "Finish the appraisal"}
          </button>
        </section>
      )}
    </div>
  );
}
